import configSettingApi, { EnvironmentParams, LogisticSettingsParams } from "@api/configSettingApi";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";

interface IState {
  isFetching: boolean;
  environmentResult: EnvironmentParams;
  logisticSettingsResult: LogisticSettingsParams | null;
  isLogisticSettingsFetching: boolean;
  isLogisticSettingsSubmitting: boolean;
  previewEstimatedArrival: string;
}
export const initialState: IState = {
  isFetching: false,
  environmentResult: {
    defaultRecommendPageIds: [],
    vipPromotionStartDate: "",
    vipPromotionContent: "",
    defaultAdditionPurchaseApplyMaxQty: "",
    promotionMaxNumberOfBindRules: "",
    vipPromotionImage: "",
    cvsLimitLongestEdge: "",
    cvsLimitSecondLongestEdge: "",
    cvsLimitShortestEdge: "",
    cvsLimitVolume: "",
    cvsLimitWeight: "",
    cvsLimitPrice: "",
    newCustomerPromotionRuleIds: [],
    vipCustomerRenewPromotionRuleIds: [],
    searchKeywords: [],
    vipPromotionEndDate: "",
    deployCvsSuspendStartDate: "",
    deployCvsSuspendEndDate: "",
    serverFields: [],
  },
  logisticSettingsResult: null,
  isLogisticSettingsFetching: false,
  isLogisticSettingsSubmitting: false,
  previewEstimatedArrival: "",
};

export const fetchEnvironmentList = createAsyncThunk("systemSetting/fetchEnvironmentList", async () => {
  const response = await configSettingApi.fetchEnvironmentList();
  return response;
});

export const fetchUpdateEnvironmentList = createAsyncThunk(
  "systemSetting/fetchUpdateEnvironmentList",
  async (params: EnvironmentParams, thunkApi) => {
    const response = await configSettingApi.fetchUpdateEnvironmentList(params);
    message.success("已儲存");
    thunkApi.dispatch(fetchEnvironmentList());
    return response;
  },
);
export const fetchPurgeLoginCache = createAsyncThunk("systemSetting/fetchPurgeLoginCache", async () => {
  try {
    await configSettingApi.fetchPurgeLoginCache();
    message.success("已清除");
  } catch (error: any) {
    message.error(error.message);
  }
});

export const fetchLogisticSettings = createAsyncThunk("systemSetting/fetchLogisticSettings", async () => {
  const response = await configSettingApi.fetchLogisticSettings();
  return response;
});

export const postLogisticSettings = createAsyncThunk(
  "systemSetting/postLogisticSettings",
  async (params: LogisticSettingsParams, thunkApi) => {
    try {
      const response = await configSettingApi.postLogisticSettings(params);
      message.success("已儲存");
      thunkApi.dispatch(fetchLogisticSettings());
      return response;
    } catch (error: any) {
      message.error(error?.message ?? "請稍後再試");
      return error;
    }
  },
);

export const postLogisticSettingsPreview = createAsyncThunk(
  "systemSetting/postLogisticSettingsPreview",
  async (params: { body: LogisticSettingsParams; isHome: boolean; estOrderDate: string }) => {
    const response = await configSettingApi.postLogisticSettingsPreview(params);
    return response;
  },
);

const configSettingSlice = createSlice({
  name: "systemSetting",
  initialState,
  reducers: {
    reset: () => initialState,
    updateEnvironmentList: (state, action: PayloadAction<EnvironmentParams>) => {
      state.environmentResult = action.payload;
    },
    clearPreviewEstimatedArrival: (state) => {
      state.previewEstimatedArrival = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEnvironmentList.fulfilled, (state, action) => {
      state.environmentResult = action.payload;
    });
    builder.addCase(fetchLogisticSettings.pending, (state) => {
      state.isLogisticSettingsFetching = true;
    });
    builder.addCase(fetchLogisticSettings.fulfilled, (state, action) => {
      state.isLogisticSettingsFetching = false;
      state.logisticSettingsResult = action.payload;
    });
    builder.addCase(fetchLogisticSettings.rejected, (state) => {
      state.isLogisticSettingsFetching = false;
    });
    builder.addCase(postLogisticSettings.pending, (state) => {
      state.isLogisticSettingsSubmitting = true;
    });
    builder.addCase(postLogisticSettings.fulfilled, (state) => {
      state.isLogisticSettingsSubmitting = false;
    });
    builder.addCase(postLogisticSettings.rejected, (state) => {
      state.isLogisticSettingsSubmitting = false;
    });
    builder.addCase(postLogisticSettingsPreview.fulfilled, (state, action) => {
      state.previewEstimatedArrival = action.payload;
    });
    builder.addCase(postLogisticSettingsPreview.rejected, (state) => {
      state.previewEstimatedArrival = "";
    });
  },
});

export const { updateEnvironmentList, clearPreviewEstimatedArrival } = configSettingSlice.actions;

export default configSettingSlice.reducer;
